import styled from '@emotion/styled';
import { Lock } from 'react-feather';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { Constants } from '@trinity/chat-components/components/Theme';

const TEMPLATES = [
  { id: 'message-selection', name: 'Message Selection', description: 'Test and rank marketing messages with your synthetic audience to identify the most compelling and relevant content for each segment.', locked: false },
  { id: 'brand-awareness', name: 'Brand Awareness Survey', description: 'Measure unaided and aided brand recall, familiarity levels, and perception strength across HCP segments to benchmark positioning.', locked: true },
  { id: 'patient-journey', name: 'Patient Journey Survey', description: 'Map key decision points, treatment triggers, and switching behaviors along the patient pathway from diagnosis through ongoing therapy.', locked: true },
  { id: 'hcp-engagement', name: 'HCP Engagement Survey', description: 'Evaluate preferred communication channels, content formats, and engagement frequency to optimize rep and digital touchpoints.', locked: true },
  { id: 'treatment-satisfaction', name: 'Treatment Satisfaction', description: 'Assess satisfaction with current therapies across efficacy, tolerability, and convenience dimensions to uncover unmet needs.', locked: true },
  { id: 'competitive-landscape', name: 'Competitive Landscape', description: 'Analyze perceived differentiation, prescribing rationale, and competitive positioning across therapeutic alternatives in the category.', locked: true },
  { id: 'market-access', name: 'Market Access Survey', description: 'Explore formulary barriers, prior authorization friction, and payer influence on prescribing decisions across practice settings.', locked: true },
  { id: 'digital-channel', name: 'Digital Channel Preference', description: 'Identify preferred digital platforms, content consumption habits, and receptivity to virtual engagement across HCP segments.', locked: true },
  { id: 'patient-adherence', name: 'Patient Adherence Survey', description: 'Investigate adherence drivers and barriers including side effect management, dosing complexity, and patient support program impact.', locked: true },
  { id: 'unmet-needs', name: 'Unmet Needs Assessment', description: 'Discover gaps in current treatment paradigms, desired product attributes, and priority clinical outcomes from the prescriber perspective.', locked: true },
];

type Props = {
  onTemplateSelect: (templateId: string) => void;
  onCancel: () => void;
};

export const SurveyTemplateSelector = ({ onTemplateSelect, onCancel }: Props) => {
  return (
    <Root>
      <Content>
        <Header>
          <StyledAutoAwesomeIcon />
          <Title>Survey Templates</Title>
        </Header>
        <ScrollArea>
          {TEMPLATES.map((template) => (
            <TemplateCard
              key={template.id}
              $locked={template.locked}
              onClick={() => {
                if (!template.locked) onTemplateSelect(template.id);
              }}
            >
              <TemplateCardContent>
                <TemplateNameRow>
                  <TemplateName $locked={template.locked}>{template.name}</TemplateName>
                  {template.locked && <Lock size={14} />}
                </TemplateNameRow>
                <TemplateDescription $locked={template.locked}>{template.description}</TemplateDescription>
              </TemplateCardContent>
            </TemplateCard>
          ))}
        </ScrollArea>
        <Footer>
          <CancelButton onClick={onCancel}>Cancel</CancelButton>
        </Footer>
      </Content>
    </Root>
  );
};

const Root = styled.div`
  box-sizing: border-box;
  border-radius: ${Constants.Modal.BorderRadius}px;
  height: 100%;
  width: 100%;
  position: relative;
`;

const Content = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  height: 100%;
  width: 100%;
  background: var(--pri-01);
`;

const StyledAutoAwesomeIcon = styled(AutoAwesomeIcon)(({ theme }) => ({
  fontSize: 20,
  color: theme.palette.primary.main,
}));

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 8px 0;
  margin-bottom: 8px;
`;

const Title = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
  flex: 1;
`;

const ScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 5px 8px 8px;
`;

const TemplateCard = styled.div<{ $locked?: boolean }>`
  display: flex;
  background: ${(p) => (p.$locked ? '#fafafa' : '#ffffff')};
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  padding: 14px;
  margin-bottom: 8px;
  cursor: ${(p) => (p.$locked ? 'not-allowed' : 'pointer')};
  opacity: ${(p) => (p.$locked ? 0.6 : 1)};
  transition: all 0.2s;
  color: ${(p) => (p.$locked ? '#999' : '#666')};

  &:hover {
    ${(p) =>
      !p.$locked &&
      `
      border-color: #6366f1;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      transform: translateY(-1px);
    `}
  }

  &:active {
    ${(p) => !p.$locked && 'transform: translateY(0);'}
  }
`;

const TemplateCardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
`;

const TemplateNameRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const TemplateName = styled.div<{ $locked?: boolean }>`
  font-size: 14px;
  font-weight: 600;
  color: ${(p) => (p.$locked ? '#999' : '#1a1a1a')};
`;

const TemplateDescription = styled.div<{ $locked?: boolean }>`
  font-size: 12px;
  line-height: 1.5;
  color: ${(p) => (p.$locked ? '#bbb' : '#666')};
`;

const Footer = styled.div`
  padding: 8px;
  border-top: 1px solid #e5e5e5;
  display: flex;
  justify-content: flex-end;
`;

const CancelButton = styled.button`
  padding: 6px 16px;
  border: 1px solid #d3d3d3;
  border-radius: 6px;
  background: transparent;
  color: #626262;
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    border-color: #999;
    background: #fafafa;
  }
`;
